<template>
  <!--
    Effectively renderless. Everything this component produces is a driver.js overlay or a
    PrimeVue dialog, both of which attach to the body rather than to this component's tree; the
    hidden span exists only because a Vue template needs a root element.
  -->
  <span hidden aria-hidden="true" />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDialog } from 'primevue/usedialog'
import { useConsoleTourStore, TOUR_MIN_VIEWPORT_WIDTH } from '@/stores/consoleTour'
import { useConsoleTourRunner } from '@/composables/useConsoleTour'
import { promptConsoleTourInvitation } from '@/components/tour/tour-invitation-dialog'

/**
 * Decides whether to offer the console orientation tour, and offers it.
 *
 * Mounted once in the authenticated shell (Full.vue). The Help menu starts the tour directly,
 * without going through here -- someone who asked for it does not need to be asked again.
 */

/**
 * The route people land on after signing in, and the only place we interrupt. Offering a tour to
 * someone who deep-linked into a timecard they came here to fix is how in-app guidance earns its
 * reputation.
 */
const LANDING_ROUTE_NAME = 'dashboard'

/** Let the dashboard's widgets finish loading before covering them with an overlay. */
const SETTLE_DELAY_MS = 1500

const route = useRoute()
const dialog = useDialog()
const tourStore = useConsoleTourStore()
const { run, stop } = useConsoleTourRunner(dialog)

let settleTimer: number | undefined
let offered = false

function viewportIsWideEnough (): boolean {
  return window.innerWidth >= TOUR_MIN_VIEWPORT_WIDTH
}

/** True when some other dialog already owns the screen. Two modals stacked is nobody's idea. */
function anotherDialogIsOpen (): boolean {
  return !!document.querySelector('.p-dialog, .modal.show')
}

async function offerTour () {
  if (offered) {
    return
  }
  offered = true

  // markOffered spends one of the limited auto-offers, so it happens here rather than on the
  // answer -- closing the tab on the invitation still counts as having been asked.
  await tourStore.markOffered()

  const answer = await promptConsoleTourInvitation(dialog, { variant: tourStore.variant })

  if (answer === 'accept') {
    await run('auto')
  } else if (answer === 'decline') {
    await tourStore.markDeclined()
  }
  // 'unanswered' leaves the record alone, so the offer comes back another day.
}

function considerOffering () {
  window.clearTimeout(settleTimer)

  if (offered || !tourStore.canAutoOffer) {
    return
  }
  // Narrow viewports get nothing automatically: the sidebar is a drawer and the header collapses,
  // so there is nothing to point at. Deliberately not counted as an offer -- they will be asked
  // properly the next time they are at a computer.
  if (!viewportIsWideEnough()) {
    return
  }
  if (route.name !== LANDING_ROUTE_NAME) {
    return
  }

  settleTimer = window.setTimeout(() => {
    if (
      !offered &&
      tourStore.canAutoOffer &&
      route.name === LANDING_ROUTE_NAME &&
      viewportIsWideEnough() &&
      !anotherDialogIsOpen()
    ) {
      void offerTour()
    }
  }, SETTLE_DELAY_MS)
}

// The session request, the preferences and the route all land at their own pace, so re-check
// rather than deciding once on mount.
watch(
  () => [tourStore.canAutoOffer, route.name],
  considerOffering,
  { immediate: true }
)

onMounted(considerOffering)

onBeforeUnmount(() => {
  window.clearTimeout(settleTimer)
  stop()
})
</script>
